import React, { useEffect, useState } from "react";
import GlobalNav from "../components/GlobalNav";
import AlbumCard from "../components/AlbumCard";
import Footer from "../components/Footer";
import "../styles/buttons.css";
import "./ShopPage.css";

// ─── DATA ────────────────────────────────────────────────────────────────────
// Same real merch catalog as MusicPage — ported here since /shop is the
// actual buying destination. Album releases stay real/API-driven too
// (GET /api/albums, GET /api/releases/featured) rather than the old
// disconnected PRODUCTS placeholder.

const LOGOS = {
  WSL: "/images/logos/WSL_print.png",
  KWE: "/images/logos/KWE_print.png",
  MIHU: "/images/logos/MIHU_print.png",
  UTM: "/images/logos/UTM_print.png",
};

const BASE =
  "https://raw.githubusercontent.com/urbangriot/Drive/cd540668bc3c66bc938b05e78a9a5624e3c5f653/musicishealing";

const SHIRT_COLORS = [
  "ash","army","asphalt","athletic_heather","black","brown","burnt_orange",
  "cardinal","coral","dark_grey","deep_teal","evergreen","forest","gold",
  "kelly","leaf","maize","military","mint","natural","olive","orange",
  "soft_cream","tan","teal","team_purple","white","yellow",
].map((key) => ({
  key,
  label: key.replace(/_/g, " "),
  src: `${BASE}/images/trendsetter/images/carousel/${key.includes("black") ? "black-small" : key.includes("white") ? "white-small" : key}.png`,
  full: `${BASE}/images/trendsetter/images/${key}.png`,
}));

const HOODIE_COLORS = [
  "cardinal","neon_green","carolina","vegas_gold","charcoal","fuschia","grey",
  "surf_blue","forest","kelly","yellow","royal","chocolate","maroon","navy",
  "red","washed_blue","purple","olive","white","black",
].map((key) => ({
  key,
  label: key.replace(/_/g, " "),
  src: `${BASE}/images/trendsetter/images/hoodies/carousel/${key}.png`,
  full: `${BASE}/images/trendsetter/images/hoodies/${key}.png`,
}));

const HERBAL_PRODUCTS = [
  {
    id: "jar", productApi: "5e8a694ce7179a7912b715b7", type: "Jar",
    name: "Herbal Jar",
    desc: "Loose herbal blend for rolling, pipe or tea infusions (papers not included).",
    image: "/images/products/Jar.png",
  },
  {
    id: "preroll", productApi: "68ecf933c18f8295fde97a94", type: "Prerolls",
    name: "Herbal Preroll Pack",
    desc: "Ready-to-use prerolls (contains 10 in a pack).",
    image: "/images/products/Prerolls.png",
  },
];

const SIZES = ["S","M","L","XL","2XL"];
const FILTERS = ["All", "Vinyl", "CDs", "Downloads", "Books", "Apparel", "Blends"];

// Print/book-type products. Dirges of Becoming is a real product with a
// real price, verified from its actual Bandcamp listing — external link,
// not our own /add/:id cart, since we don't fulfill this ourselves.
const BOOKS = [
  {
    id: "dirges-of-becoming",
    title: "Dirges of Becoming (Book/Soundtrack)",
    desc: "kwe's debut poetry collection, paired with a 7-track companion EP available as CD or digital download.",
    image: "https://f4.bcbits.com/img/0027816064_36.jpg",
    price: "$25.00",
    externalUrl: "https://aquarianproject.bandcamp.com/merch/dirges-of-becoming-book-soundtrack",
  },
];

// ─── HELPERS ─────────────────────────────────────────────────────────────────

function mapAlbumToCard(album) {
  return {
    title: album.title,
    artist: album.artists?.length ? album.artists.join(", ") : "Music Is Healing",
    format: [
      album.producttype,
      album.date ? new Date(album.date).getFullYear() : null,
      album.format,
    ].filter(Boolean).join(" · "),
    price: album.price != null ? `$${Number(album.price).toFixed(2)}` : undefined,
    remaining: album.inventory,
    total: album.initial_inventory,
    coverSrc: album.imagepath,
  };
}

// ─── COMPONENT: per-format purchase card ────────────────────────────────────
// Ported from the old LandingPage.jsx's album carousel: if an album's
// `format` column includes CD or Vinyl, show checkboxes + quantity per
// physical format and an Add to Cart button. Otherwise it's digital-only
// (stream/download), so just show a link — nothing to add to cart.

function ReleasePurchaseCard({ album, onAddToCart }) {
  const p = mapAlbumToCard(album);
  const formats = album.format
    ? String(album.format).split(",").map((f) => f.trim())
    : [];
  const physicalFormats = formats.filter((f) =>
    ["cd", "vinyl"].includes(f.toLowerCase())
  );
  const hasDigital = formats.some((f) =>
    ["download", "stream"].includes(f.toLowerCase())
  );
  const hasPhysical = physicalFormats.length > 0;

  function handleAdd() {
    const selections = physicalFormats
      .map((fmt) => {
        const checkbox = document.getElementById(`shop-fmt-${album.id}-${fmt}`);
        const qtyInput = document.getElementById(`shop-qty-${album.id}-${fmt}`);
        if (checkbox?.checked) {
          return { format: fmt, qty: parseInt(qtyInput?.value || "1") };
        }
        return null;
      })
      .filter(Boolean);
    onAddToCart(album, selections);
  }

  return (
    <div className="shop-release-card">
      <a
        className="shop-release-link"
        href={album.url || "#"}
        target={album.url ? "_blank" : undefined}
        rel={album.url ? "noopener noreferrer" : undefined}
      >
        <div className="shop-release-art"
          style={!album.imagepath ? { background: "var(--mihu-navy, #2F3A78)" } : undefined}
        >
          {album.imagepath
            ? <img src={album.imagepath} alt={album.title} />
            : <span aria-hidden="true">♪</span>}
        </div>
        <div className="shop-release-title">{album.title}</div>
        <div className="shop-release-artist">{p.artist}</div>
        <div className="shop-release-meta">{p.format}</div>
        {p.price && <div className="shop-release-price">{p.price}</div>}
      </a>

      {hasPhysical && (
        <div className="shop-format-panel">
          <div className="shop-format-label">Available formats</div>
          {physicalFormats.map((fmt) => (
            <div className="shop-format-row" key={fmt}>
              <label>
                <input type="checkbox" id={`shop-fmt-${album.id}-${fmt}`} />
                {fmt.toUpperCase()}
              </label>
              <input
                type="number"
                id={`shop-qty-${album.id}-${fmt}`}
                min="1"
                defaultValue="1"
                className="shop-format-qty"
              />
            </div>
          ))}

          {hasDigital && (
            <a
              className="shop-digital-link"
              href={album.url || "#"}
              target="_blank"
              rel="noopener noreferrer"
            >
              🎵 Digital download / stream available
            </a>
          )}

          <button className="mihu-btn-primary-lg shop-format-cta" onClick={handleAdd}>
            Add to cart
          </button>
        </div>
      )}

      {!hasPhysical && hasDigital && (
        <a
          className="shop-digital-link shop-digital-link--standalone"
          href={album.url || "#"}
          target="_blank"
          rel="noopener noreferrer"
        >
          🎵 Digital download / stream available
        </a>
      )}
    </div>
  );
}

// ─── COMPONENT ───────────────────────────────────────────────────────────────

// Writes to the same localStorage 'cart' key GlobalNav reads for its
// badge count, and fires a custom event so the badge updates immediately
// instead of only on the next page load/navigation (GlobalNav previously
// only read this once on mount). None of this page's three add-to-cart
// handlers were touching localStorage at all before — they only POSTed
// to the backend, so the navbar count could never move no matter what
// was actually added.
function addToLocalCart(item) {
  try {
    const existing = JSON.parse(localStorage.getItem("cart") || "[]");
    const cart = Array.isArray(existing) ? existing : [];
    cart.push({ ...item, addedAt: Date.now() });
    localStorage.setItem("cart", JSON.stringify(cart));
    window.dispatchEvent(new Event("cart:updated"));
  } catch (e) {
    console.error("Error updating local cart:", e);
  }
}

export default function ShopPage() {
  // Releases
  const [filter, setFilter] = useState("All");
  const [albums, setAlbums] = useState(null);
  const [featured, setFeatured] = useState(null);

  // Merch
  const [category, setCategory] = useState("shirts");
  const [body, setBody] = useState("white");
  const [logo, setLogo] = useState(null);
  const [size, setSize] = useState("");
  const [toast, setToast] = useState(false);

  useEffect(() => {
    fetch("/api/albums")
      .then((r) => r.json())
      .then((d) => setAlbums(Array.isArray(d) ? d : []))
      .catch((e) => console.error("albums:", e));
    fetch("/api/releases/featured")
      .then((r) => r.json())
      .then((d) => setFeatured(d))
      .catch((e) => console.error("featured:", e));
  }, []);

  const allAlbums = albums ?? [];

  function albumFormats(album) {
    if (!album.format) return [];
    return String(album.format).split(",").map((f) => f.trim().toLowerCase());
  }

  const visibleAlbums = allAlbums.filter((a) => {
    const formats = albumFormats(a);
    if (filter === "Vinyl") return formats.includes("vinyl");
    if (filter === "CDs") return formats.includes("cd");
    if (filter === "Downloads") return formats.includes("download") || formats.includes("stream");
    if (filter === "Books" || filter === "Apparel" || filter === "Blends") return false; // shown in their own sections below
    return true; // "All"
  });
  const showReleases = filter === "All" || filter === "Vinyl" || filter === "CDs" || filter === "Downloads";
  const showBooks = filter === "All" || filter === "Books";
  const showBlends = filter === "All" || filter === "Books" || filter === "Blends";
  const showMerch = filter === "All" || filter === "Apparel";

  const featuredProps = featured ? mapAlbumToCard(featured) : null;

  // Merch helpers
  const colors = category === "hoodies" ? HOODIE_COLORS : SHIRT_COLORS;
  const selectedColor = colors.find((c) => c.key === body) || colors[0];

  // Per-format add-to-cart, mirroring the old LandingPage.jsx's
  // handleAddAlbumToCart — same real /add/:id endpoint pattern already
  // used by the merch panel below.
  async function handleAddAlbumFormats(album, selections) {
    if (!selections.length) {
      alert("Please select at least one format before adding to cart.");
      return;
    }
    try {
      for (const { format, qty } of selections) {
        await fetch(`/add/${album.id}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            product: album.id,
            productType: "album",
            size: format,
            designSpecs: `${album.title} - ${format.toUpperCase()} (${qty} unit(s))`,
            albumTitle: album.title,
            format,
            quantity: qty,
          }),
        });
        addToLocalCart({
          id: album.id,
          title: `${album.title} · ${format.toUpperCase()}`,
          qty,
        });
      }
      setToast(true); setTimeout(() => setToast(false), 3000);
    } catch (e) {
      alert("Network error adding to cart.");
    }
  }

  async function handleAddToCart() {
    if (!logo || !size) {
      alert("Please select a logo and size before adding to cart.");
      return;
    }
    const productType = category === "hoodies" ? "hoodie" : "tee";
    const productApi = category === "hoodies"
      ? "62abe86ad31113d8b87abb36"
      : "5e9683d1e7179a2493c7da47";
    const bodyLabel = body.replace(/_/g, " ");
    const payload = {
      product: body, size, productType, productPrint: logo,
      bodyColor: bodyLabel,
      designSpecs: `${bodyLabel} ${productType} with ${logo} print`,
    };
    try {
      const res = await fetch(`/add/${productApi}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        addToLocalCart({ id: productApi, title: `${bodyLabel} ${productType} · ${logo}`, qty: 1 });
        setToast(true); setTimeout(() => setToast(false), 3000);
      }
      else alert("Failed to add to cart.");
    } catch (e) { alert("Network error."); }
  }

  async function handleAddHerbal() {
    const selections = HERBAL_PRODUCTS.filter(
      (p) => document.getElementById(`shop-merch-select-${p.id}`)?.checked
    ).map((p) => ({
      ...p,
      qty: parseInt(document.getElementById(`shop-merch-qty-${p.id}`)?.value || "1"),
    }));
    if (!selections.length) { alert("Please select at least one item."); return; }
    try {
      for (const item of selections) {
        await fetch(`/add/${item.productApi}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            product: item.id, productType: item.type,
            size: `${item.qty} unit(s)`,
            designSpecs: `${item.qty} x ${item.name}`,
          }),
        });
        addToLocalCart({ id: item.id, title: item.name, qty: item.qty });
      }
      setToast(true); setTimeout(() => setToast(false), 3000);
    } catch (e) { alert("Network error."); }
  }

  return (
    <div className="shop-page">
      <GlobalNav activeHref="/shop" ctaLabel="Join the collective" />

      {/* ── Hero ── */}
      {/* <div className="shop-hero">
        <div className="shop-hero-inner">
          <div className="shop-hero-eyebrow">Support the collective</div>
          <h1 className="shop-hero-title">Shop</h1>
          <p className="shop-hero-sub">Vinyl, CDs, books, merch, and herbal blends</p>
        </div>

      </div> */}

      <div className="shop-body">
        <div className="shop-filters">
          {FILTERS.map((f) => (
            <button key={f}
              className={"shop-filter-btn" + (filter === f ? " active" : "")}
              onClick={() => setFilter(f)}
            >{f}</button>
          ))}
        </div>
        <br/><br/>
        {/* ── Featured release ── */}
        {featuredProps && (
          <>
            <div className="shop-section-label">Featured</div>
            <AlbumCard layout="featured" {...featuredProps}
              ctaLabel="Pre-order" secondaryCtaLabel="Stream" />
          </>
        )}

        {/* ── All releases — per-format purchase, ported from the old
             LandingPage.jsx's album carousel logic ── */}
        {showReleases && (
          <>
            <div className="shop-section-label" style={{ marginTop: "var(--space-5, 24px)" }}>
              Music releases
            </div>
            {albums === null && <p className="shop-loading">Loading releases…</p>}
            {albums !== null && visibleAlbums.length === 0 && <p className="shop-empty">No releases found.</p>}
            {visibleAlbums.length > 0 && (
              <div className="shop-releases-grid">
                {visibleAlbums.map((album) => (
                  <ReleasePurchaseCard
                    key={album.id}
                    album={album}
                    onAddToCart={handleAddAlbumFormats}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* ── Books — one real item so far (Drum Talk Workbook), which is
             currently free as a PDF on the Storytime page. No paid print
             SKU exists yet — send real catalog data to replace this. ── */}
        {showBooks && (
          <>
            <div className="shop-section-label" style={{ marginTop: "var(--space-7, 48px)" }}>
              Books
            </div>
            <div className="shop-books-grid">
              {BOOKS.map((book) => (
                <div className="shop-book-card" key={book.id}>
                  <img src={book.image} alt={book.title} className="shop-book-img" />
                  <div className="shop-book-body">
                    <div className="shop-book-title">{book.title}</div>
                    <p className="shop-book-desc">{book.desc}</p>
                    {book.price && <div className="shop-book-price">{book.price}</div>}
                    <a
                      className="mihu-btn-primary-lg"
                      href={book.externalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Buy on Bandcamp
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ── Blends — moved out from under Merch's tabs, now its own
             section positioned under Books ── */}
        {showBlends && (
          <>
            <div className="shop-section-label" style={{ marginTop: "var(--space-7, 48px)" }}>
              Blends
            </div>
            <div className="merch-herbal">
              <div className="merch-herbal-header">
                <img src="/images/products/Jar.png" alt="Holy Smokes herbal blend" className="merch-herbal-hero-img" />
                <div>
                  <h3 className="merch-herbal-title">Holy Smokes</h3>
                  <p className="merch-herbal-desc">
                    A grounding shaman's blend to support the lungs and boost positive mood.
                    Perfect for meditation, yoga, or creative flow sessions. 100% tobacco-free.
                    Contains: Mullein, Mugwort, Lavender, Raspberry Leaf, Sage, Yerba Santa, Hops.
                  </p>
                </div>
              </div>

              <div className="merch-herbal-products">
                {HERBAL_PRODUCTS.map((item) => (
                  <label key={item.id} className="merch-herbal-row">
                    <img src={item.image} alt={item.name} className="merch-herbal-img" />
                    <div className="merch-herbal-info">
                      <div className="merch-herbal-name">{item.name}</div>
                      <div className="merch-herbal-meta">{item.desc}</div>
                    </div>
                    <div className="merch-herbal-controls">
                      <input type="checkbox" id={`shop-merch-select-${item.id}`} />
                      <input type="number" id={`shop-merch-qty-${item.id}`}
                        min="1" defaultValue="1" className="merch-herbal-qty" />
                    </div>
                  </label>
                ))}
              </div>

              <button className="mihu-btn-primary-lg merch-cta" onClick={handleAddHerbal}>
                Add selected to cart
              </button>
            </div>
          </>
        )}

        {/* ── Merch / Boutique ── */}
        {showMerch && (
        <>
        <div className="shop-section-label" style={{ marginTop: "var(--space-7, 48px)" }}>
          Merch
        </div>
        <div className="merch-panel">

          {/* Category tabs */}
          <div className="merch-tabs">
            {[["shirts","Tees"],["hoodies","Hoodies"]].map(([key, label]) => (
              <button key={key}
                className={"merch-tab" + (category === key ? " active" : "")}
                onClick={() => { setCategory(key); setBody("white"); }}
              >{label}</button>
            ))}
          </div>

          {/* ── Shirts / Hoodies ── */}
          {(category === "shirts" || category === "hoodies") && (
            <div className="merch-apparel">

              {/* Color picker */}
              <div className="merch-color-picker">
                <div className="merch-sub-label">Color</div>
                <div className="merch-color-swatches">
                  {colors.map((c) => (
                    <button key={c.key} title={c.label}
                      className={"merch-swatch" + (body === c.key ? " selected" : "")}
                      onClick={() => setBody(c.key)}
                    >
                      <img src={c.src} alt={c.label} />
                    </button>
                  ))}
                </div>
              </div>

              <div className="merch-apparel-main">
                {/* Preview */}
                <div className="merch-preview">
                  <img className="merch-preview-garment" src={selectedColor.full} alt={selectedColor.label} />
                  {logo && (
                    <img className="merch-preview-logo" src={LOGOS[logo]} alt={logo} />
                  )}
                </div>

                <div className="merch-options">
                  {/* Logo */}
                  <div className="merch-sub-label">Logo</div>
                  <div className="merch-logo-row">
                    {Object.entries(LOGOS).map(([key, src]) => (
                      <button key={key}
                        className={"merch-logo-btn" + (logo === key ? " selected" : "")}
                        onClick={() => setLogo(key)}
                      >
                        <img src={src} alt={key} />
                        <span>{key}</span>
                      </button>
                    ))}
                  </div>

                  {/* Size */}
                  <div className="merch-sub-label" style={{ marginTop: "var(--space-3, 12px)" }}>Size</div>
                  <div className="merch-size-row">
                    {SIZES.map((s) => (
                      <button key={s}
                        className={"merch-size-btn" + (size === s ? " selected" : "")}
                        onClick={() => setSize(s)}
                      >{s}</button>
                    ))}
                  </div>

                  {/* Specs */}
                  {(logo || size) && (
                    <div className="merch-specs">
                      {[size, selectedColor.label, category === "hoodies" ? "hoodie" : "T-shirt", logo && `· ${logo} print`]
                        .filter(Boolean).join(" ")}
                    </div>
                  )}

                  <button className="mihu-btn-primary-lg merch-cta" onClick={handleAddToCart}>
                    Add to cart
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        </>
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div className="merch-toast" role="status">
          Added to cart ✓
        </div>
      )}

      <Footer />
    </div>
  );
}
